import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import MattePaperCard from "./MattePaperCard";
import "./TimelineRoadmap.css";

gsap.registerPlugin(ScrollTrigger);

// Define your roadmap milestones
const MILESTONES = [
  {
    phase: "PHASE 01",
    title: "Concept",
    description:
      "Initial ideation and research phase. Defining project scope and requirements.",
    tags: ["Research", "Planning"],
  },
  {
    phase: "PHASE 02",
    title: "Design",
    description:
      "UI/UX design and prototyping. Creating wireframes and visual identity.",
    tags: ["Figma", "Prototypes"],
  },
  {
    phase: "PHASE 03",
    title: "Development",
    description:
      "Building the application. Frontend and backend implementation.",
    tags: ["React", "Node.js"],
  },
  {
    phase: "PHASE 04",
    title: "Launch",
    description:
      "Deployment and release. Going live with continuous monitoring.",
    tags: ["Deploy", "Scale"],
  },
];

const TimelineRoadmap = () => {
  const outerWrapperRef = useRef(null);
  const wrapperRef = useRef(null);
  const svgPathRef = useRef(null);
  const mobileLineRef = useRef(null); // Ref for mobile vertical SVG line

  // Mobile detection
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 900 : false
  );

  // Update mobile state on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Skip horizontal scroll setup on mobile - CSS handles vertical layout
    if (isMobile) return;

    if (!outerWrapperRef.current || !wrapperRef.current || !svgPathRef.current)
      return;

    const outerWrapper = outerWrapperRef.current;

    const wrapper = wrapperRef.current;
    const drawPath = svgPathRef.current;
    const pathLength = drawPath.getTotalLength();

    // --- CONFIGURATION ---
    // Add a small percentage offset to push dots Right (+) or Left (-)
    // 0.01 = 1% nudge to the right
    // 0.02 = 2% nudge to the right (Start here if you want them more to the right)
    const dotOffsets = [
      0.0, // Dot 1 Nudge
      0.005, // Dot 2 Nudge
      0.005, // Dot 3 Nudge
      0.0075, // Dot 4 Nudge
    ];

    // 1. Reset Path
    gsap.set(drawPath, {
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
    });

    // 2. Horizontal Scroll
    // Get the parent #stack section to toggle its border
    const stackSection = outerWrapper.closest("#stack");

    const scrollTween = gsap.to(wrapper, {
      xPercent: -83.33, // (5 sections moved / 6 total sections) * 100
      ease: "none",
      scrollTrigger: {
        trigger: wrapper,
        pin: true,
        scrub: 0.5, // Reduced for tighter sync
        end: "+=4000",
        refreshPriority: 1,
        onEnter: () => {
          outerWrapper.classList.add("is-pinned");
          if (stackSection)
            stackSection.style.cssText += "border: none !important;";
        },
        onLeave: () => {
          outerWrapper.classList.remove("is-pinned");
          if (stackSection)
            stackSection.style.cssText = stackSection.style.cssText.replace(
              /border:[^;]+!important;?/g,
              ""
            );
        },
        onEnterBack: () => {
          outerWrapper.classList.add("is-pinned");
          if (stackSection)
            stackSection.style.cssText += "border: none !important;";
        },
        onLeaveBack: () => {
          outerWrapper.classList.remove("is-pinned");
          if (stackSection)
            stackSection.style.cssText = stackSection.style.cssText.replace(
              /border:[^;]+!important;?/g,
              ""
            );
        },
      },
    });

    // 3. Line Drawing with Dot Activation
    gsap.to(drawPath, {
      strokeDashoffset: 0,
      ease: "none",
      scrollTrigger: {
        trigger: wrapper,
        start: "top top",
        end: "+=4000",
        scrub: 0.5, // Must match scrollTween scrub
        onUpdate: (self) => {
          // Calculate how much of the line has been drawn (0 = none, 1 = complete)
          const progress = self.progress;

          // Activate dots when line passes through them
          const regularDots = wrapper.querySelectorAll(
            ".roadmap-dots-layer > .roadmap-dot"
          );

          // Helper for trigger points matches updateDotPositions logic
          const getTriggerPoint = (index) => {
            const sectionIndex = index + 1;
            const base = sectionIndex / 6;
            const offset = dotOffsets[index] || 0;
            return base + offset;
          };

          regularDots.forEach((dot, i) => {
            const triggerPoint = getTriggerPoint(i);
            const threshold = 0.02;

            if (
              progress >= triggerPoint - threshold &&
              progress <= triggerPoint + threshold
            ) {
              gsap.to(dot, {
                scale: 1.5,
                backgroundColor: "#111",
                duration: 0.3,
                ease: "power2.out",
                overwrite: true,
              });
            } else if (progress > triggerPoint + threshold) {
              gsap.set(dot, {
                scale: 1.5,
                backgroundColor: "#111",
                overwrite: true,
              });
            } else {
              gsap.to(dot, {
                scale: 1,
                backgroundColor: "#e9e1d7",
                duration: 0.3,
                ease: "power2.out",
                overwrite: true,
              });
            }
          });

          // ANIMATE CARDS
          const milestones = wrapper.querySelectorAll(".roadmap-milestone");
          milestones.forEach((cardWrapper, i) => {
            const triggerPoint = getTriggerPoint(i);

            // When progress passes the dot, show the card
            if (progress > triggerPoint) {
              gsap.to(cardWrapper, {
                autoAlpha: 1,
                scale: 1,
                // yPercent is handled by updateDotPositions (0)
                duration: 0.5,
                ease: "back.out(1.7)",
                overwrite: "auto",
              });
            } else {
              gsap.to(cardWrapper, {
                autoAlpha: 0,
                scale: 0.8,
                // yPercent is handled by updateDotPositions (0)
                duration: 0.3,
                ease: "power2.in",
                overwrite: "auto",
              });
            }
          });
        },
      },
    });

    // Initialize Cards State (Hidden)
    // Position handling is now in updateDotPositions
    const initialMilestones = wrapper.querySelectorAll(".roadmap-milestone");
    gsap.set(initialMilestones, { autoAlpha: 0, scale: 0.8 });

    // =========================================================
    // 4. Position Dots (RESPONSIVE WITH MANUAL NUDGE)
    // =========================================================
    const regularDots = wrapper.querySelectorAll(
      ".roadmap-dots-layer > .roadmap-dot"
    );

    const svgEl = drawPath.ownerSVGElement;
    const viewBox = svgEl.viewBox.baseVal;
    const totalSections = 6;

    // Helper: Find Y on the specific path at a specific X
    const findYAtX = (targetX) => {
      const numSamples = 1000;
      let closestPoint = { x: 0, y: 0 };
      let closestDistance = Infinity;

      for (let i = 0; i <= numSamples; i++) {
        const length = (i / numSamples) * pathLength;
        const point = drawPath.getPointAtLength(length);
        const distance = Math.abs(point.x - targetX);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestPoint = point;
        }
      }
      return closestPoint;
    };

    let updateDotPositionsRef = null;

    const updateDotPositions = () => {
      regularDots.forEach((dot, i) => {
        const sectionIndex = i + 1;

        // 1. Calculate Base Percentage (Strict Grid)
        let fraction = sectionIndex / totalSections;

        // 2. Add Manual Nudge (Responsive Offset)
        // If you set dotOffsets[i] to 0.02, it adds 2% to the position
        fraction += dotOffsets[i] || 0;

        // 3. Convert to SVG Coordinates to find the height
        const targetSVGX = fraction * viewBox.width;

        // 4. Find the exact Y height on the line for this X
        const point = findYAtX(targetSVGX);

        // 5. Apply position using percentages relative to container
        gsap.set(dot, {
          left: `${fraction * 100}%`,
          top: `${(point.y / viewBox.height) * 100}%`,
          xPercent: -50,
          yPercent: -50,
        });

        // 6. Apply position to corresponding Milestone Card
        const milestone = wrapper.querySelectorAll(".roadmap-milestone")[i];
        if (milestone) {
          // Force ALL cards to hang BELOW the dot
          // yPercent 0 means the top of the card is at the dot's position.
          const yPct = 0;

          // Move slightly to horizontal center of card since left is 0 on card
          // Actually card width is 350px. We want center of card at dot X.
          // So we translate(-50%) horizontally.

          gsap.set(milestone, {
            left: `${fraction * 100}%`,
            top: `${(point.y / viewBox.height) * 100}%`,
            xPercent: -50, // Center horizontally on the dot
            yPercent: yPct,
          });
        }
      });
    };

    updateDotPositions();
    updateDotPositionsRef = updateDotPositions;
    window.addEventListener("resize", updateDotPositions);

    // =========================================================
    // 5. TEXT FADE IN
    // =========================================================
    const outroSection = wrapper.querySelector(".roadmap-outro");
    const hintText = wrapper.querySelector(".block-hint-static");

    if (outroSection && hintText) {
      gsap.fromTo(
        hintText,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          ease: "power2.out",
          scrollTrigger: {
            trigger: outroSection,
            containerAnimation: scrollTween,
            start: "left 60%",
            toggleActions: "play reverse play reverse",
          },
        }
      );
    }

    // =========================================================
    // 7. RESTORE BORDER AT OUTRO
    // Show original #stack border when reaching the outro section
    // =========================================================
    if (outroSection && stackSection) {
      ScrollTrigger.create({
        trigger: outroSection,
        containerAnimation: scrollTween,
        start: "left 80%", // When outro enters viewport
        end: "left 20%",
        onEnter: () => {
          outerWrapper.classList.add("at-outro");
          // Remove the border:none to restore original CSS border
          stackSection.style.cssText = stackSection.style.cssText.replace(
            /border:[^;]+!important;?/g,
            ""
          );
        },
        onLeave: () => {
          // When leaving downwards (past the end), we want the border to STAY visible.
          outerWrapper.classList.remove("at-outro");
        },
        onEnterBack: () => {
          outerWrapper.classList.add("at-outro");
          stackSection.style.cssText = stackSection.style.cssText.replace(
            /border:[^;]+!important;?/g,
            ""
          );
        },
        onLeaveBack: () => {
          // When leaving upwards (back into the scroll), we want to hide the border again
          outerWrapper.classList.remove("at-outro");
          stackSection.style.cssText += "border: none !important;";
        },
      });
    }
    return () => {
      if (updateDotPositionsRef) {
        window.removeEventListener("resize", updateDotPositionsRef);
      }
      ScrollTrigger.getAll().forEach((st) => st.kill());
    };
  }, [isMobile]);

  // Mobile-specific scroll animation for vertical line color change
  useEffect(() => {
    if (!isMobile || !mobileLineRef.current || !wrapperRef.current) return;

    const mobileLine = mobileLineRef.current;
    const wrapper = wrapperRef.current;
    const pathLength = mobileLine.getTotalLength();

    // Initial state: line is not drawn
    gsap.set(mobileLine, {
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
      stroke: "#ccc", // Start gray
    });

    // Animate line drawing and color change on scroll
    ScrollTrigger.create({
      trigger: wrapper,
      start: "top 80%",
      end: "bottom 20%",
      scrub: 0.5,
      onUpdate: (self) => {
        const progress = self.progress;

        // Draw the line progressively
        const drawOffset = pathLength * (1 - progress);
        gsap.set(mobileLine, { strokeDashoffset: drawOffset });

        // Interpolate color from gray (#666) to orange (#eb5939) based on progress
        const r = Math.round(102 + (235 - 102) * progress); // 102 -> 235
        const g = Math.round(102 + (89 - 102) * progress); // 102 -> 89
        const b = Math.round(102 + (57 - 102) * progress); // 102 -> 57
        gsap.set(mobileLine, { stroke: `rgb(${r}, ${g}, ${b})` });
      },
    });

    return () => {
      ScrollTrigger.getAll().forEach((st) => st.kill());
    };
  }, [isMobile]);

  return (
    <div className="roadmap-wrapper" ref={outerWrapperRef}>
      <div className="roadmap-scroll-container" ref={wrapperRef}>
        {/* SVG Container - different for desktop (horizontal) vs mobile (hidden, CSS handles line) */}
        {!isMobile && (
          <div className="roadmap-svg-container">
            <svg
              className="roadmap-svg"
              preserveAspectRatio="none"
              viewBox="0 0 6000 1000"
            >
              <defs>
                <linearGradient
                  id="lineGradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#ccc" />
                  <stop offset="10%" stopColor="#666" />
                  <stop offset="80%" stopColor="#666" />
                  <stop offset="100%" stopColor="#eb5939" />
                </linearGradient>
              </defs>

              <path
                d="M0,500 C600,500 900,200 1500,200 C2100,200 2400,800 3000,800 C3600,800 3900,300 4500,300 C5100,300 5300,500 5300,1000"
                fill="none"
                stroke="rgba(204,204,204,0.2)"
                strokeWidth="2"
              />

              <path
                ref={svgPathRef}
                d="M0,500 C600,500 900,200 1500,200 C2100,200 2400,800 3000,800 C3600,800 3900,300 4500,300 C5100,300 5300,500 5300,1000"
                fill="none"
                stroke="url(#lineGradient)"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
        )}

        {/* Mobile vertical line - SVG for scroll-animated color change */}
        {isMobile && (
          <svg
            className="roadmap-mobile-line-svg"
            style={{
              position: "absolute",
              top: "280px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "4px",
              height: "calc(100% - 480px)",
              zIndex: 1,
              pointerEvents: "none",
              overflow: "visible",
            }}
            preserveAspectRatio="none"
          >
            <line
              ref={mobileLineRef}
              x1="2"
              y1="0"
              x2="2"
              y2="100%"
              stroke="#ccc"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        )}

        <div className="roadmap-dots-layer">
          {MILESTONES.map((_, index) => (
            <div key={index} className="roadmap-dot"></div>
          ))}
        </div>

        <section className="roadmap-section roadmap-intro">
          <div className="roadmap-intro-content">
            <div className="roadmap-badge">DEVELOPMENT PROCESS</div>
            <h2 className="roadmap-main-title">From Scratch</h2>
            <p className="roadmap-subtitle">
              Every great product starts with an idea.
            </p>
          </div>
        </section>

        {MILESTONES.map((milestone, i) => (
          <section
            key={i}
            className="roadmap-section roadmap-milestone-section"
          >
            <div
              className={`roadmap-milestone ${
                i % 2 === 0
                  ? "roadmap-milestone-bottom"
                  : "roadmap-milestone-top"
              }`}
            >
              <MattePaperCard
                phase={milestone.phase}
                title={milestone.title}
                description={milestone.description}
                tags={milestone.tags}
                maxTilt={12}
                enableTilt={true}
              />
            </div>
          </section>
        ))}

        <section className="roadmap-section roadmap-outro">
          <div className="roadmap-outro-content">
            <h2 className="roadmap-outro-title">Final Product</h2>
            <p className="roadmap-outro-text">
              {" "}
              Ready to scale and make an impact.
            </p>

            <div
              className="block-hint-static"
              style={{
                marginTop: "20px",
                color: "#eb5939",
                fontSize: "0.9rem",
                fontWeight: "bold",
                letterSpacing: "2px",
                textTransform: "uppercase",
                fontFamily: "monospace",
                cursor: "pointer",
              }}
            >
              Click & Hold Me
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TimelineRoadmap;
